"use client";

import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import type { ReactNode } from "react";
import GradientText from "@/components/GradientText";
import { REGISTER_URL } from "@/components/pricing/data";
import { Button, CODE, DARK_BAND, SectionShell, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { routePath } from "@/lib/routes";
import { gsap, NOT_REDUCED, useGSAP, useMaskedReveal } from "./kit/motion";

type ScenarioId = "create" | "update" | "analyze" | "resolve";

interface Scenario {
  id: ScenarioId;
  /** Chip row label (mono, lowercase). */
  chip: string;
  user: string;
  /** The short assistant line between the user bubble and the tool call. */
  thinking: string;
  call: { method: string; target: string; meta: string; status: string };
  /** One entry per rendered JSON line — pre-colored with CODE tokens. */
  json: ReactNode[];
  answer: ReactNode;
  /** Assistant-initiative suggestion/question after the answer — the
   *  "supporting the user to not forget anything" beat. */
  followup: ReactNode;
}

// ONE story, told in four connected MCP tool calls around a single link
// (links.acme.com/webinar): create it, route German visitors, check how it's
// doing, then confirm it's still healthy — each beat ending with the
// assistant proactively offering the next useful thing, which is also what
// the NEXT scenario picks up (the timeline loops resolve → create, closing
// the story). PayloadAnatomySection's own example (rii.link/sale) is a
// different link — no contradiction with this hero's story.
const SCENARIOS: Scenario[] = [
  {
    id: "create",
    chip: "create",
    user: "Spin up a link for the webinar signup on our domain.",
    thinking: "Creating the link —",
    call: { method: "MCP", target: "create_mapping", meta: "host links.acme.com", status: "ok" },
    json: [
      <>{"{"}</>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;hostname&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;links.acme.com&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;path&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;/webinar&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;short_url&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;https://links.acme.com/webinar&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;order&quot;</span>
        {": "}
        <span className={CODE.number}>3</span>
        {""}
      </>,
      <>{"}"}</>,
    ],
    answer: (
      <>
        Live — <span className="font-jetbrains text-[13px] text-brand-400">links.acme.com/webinar</span> is ready to
        share.
      </>
    ),
    followup: "Your plan includes routing — should German visitors go straight to the German page?",
  },
  {
    id: "update",
    chip: "update",
    user: "Good catch — yes, route German visitors to /de.",
    thinking: "Adding the country rule —",
    call: {
      method: "MCP",
      target: "set_routing_rules",
      meta: "1 rule + fallback",
      status: "ok",
    },
    json: [
      <>{"{"}</>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;rules&quot;</span>
        {": [{ "}
        <span className={CODE.key}>&quot;if&quot;</span>
        {": { "}
        <span className={CODE.key}>&quot;country&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;DE&quot;</span>
        {" },"}
      </>,
      <>
        {"    "}
        <span className={CODE.key}>&quot;to&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;https://acme.com/de/webinar&quot;</span>
        {" }],"}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;fallback_url&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;https://acme.com/webinar-signup&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;editable&quot;</span>
        {": "}
        <span className={CODE.bool}>true</span>
        {""}
      </>,
      <>{"}"}</>,
    ],
    answer: "Done — German visitors get /de, everyone else still lands on the signup page.",
    followup: "Anything going to print? A QR code points at this link, so it follows every change.",
  },
  {
    id: "analyze",
    chip: "analyze",
    user: "We're live — how's the webinar link doing?",
    thinking: "Pulling the numbers —",
    call: {
      method: "MCP",
      target: "analytics_summary",
      meta: "period 24h · this link",
      status: "ok",
    },
    json: [
      <>{"{"}</>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;totals&quot;</span>
        {": { "}
        <span className={CODE.key}>&quot;requests&quot;</span>
        {": "}
        <span className={CODE.number}>4812</span>
        {" },"}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;breakdowns&quot;</span>
        {": {"}
      </>,
      <>
        {"    "}
        <span className={CODE.key}>&quot;referrer&quot;</span>
        {": [{ "}
        <span className={CODE.key}>&quot;key&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;instagram.com&quot;</span>
        {", "}
        <span className={CODE.key}>&quot;count&quot;</span>
        {": "}
        <span className={CODE.number}>1829</span>
        {" }, …],"}
      </>,
      <>
        {"    "}
        <span className={CODE.key}>&quot;country&quot;</span>
        {": [{ "}
        <span className={CODE.key}>&quot;key&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;DE&quot;</span>
        {", "}
        <span className={CODE.key}>&quot;count&quot;</span>
        {": "}
        <span className={CODE.number}>2210</span>
        {" }, …]"}
      </>,
      <>{"  }"}</>,
      <>{"}"}</>,
    ],
    answer: "4,812 human clicks — 38% from Instagram, top country Germany. The country rule is earning its keep.",
    followup: "Want me to re-check the domain before the next campaign push?",
  },
  {
    id: "resolve",
    chip: "check",
    user: "Yes — is links.acme.com still set up right?",
    thinking: "Checking DNS and the certificate live —",
    call: { method: "MCP", target: "get_host", meta: "live_check: true", status: "ok" },
    json: [
      <>{"{"}</>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;host&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;links.acme.com&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;status&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;active&quot;</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;domain_resolves&quot;</span>
        {": "}
        <span className={CODE.bool}>true</span>
        {","}
      </>,
      <>
        {"  "}
        <span className={CODE.key}>&quot;ssl_status&quot;</span>
        {": "}
        <span className={CODE.string}>&quot;active&quot;</span>
        {""}
      </>,
      <>{"}"}</>,
    ],
    answer: "All healthy — DNS resolves and the certificate is active, so the link and its DE rule keep answering.",
    followup: "Anything else to set up? I can create the next campaign's links whenever you're ready.",
  },
];

const SR_SUMMARY =
  "One example conversation with an AI assistant connected to rii.link's MCP server, told in four tool calls " +
  "around a single link, links.acme.com/webinar. First: asked to create a webinar signup link — create_mapping " +
  "creates it, answered live at links.acme.com/webinar, then the assistant notes the plan includes routing and " +
  "asks whether German visitors should go to the German page. Second: asked to route German visitors to /de — " +
  "set_routing_rules adds a country rule with the signup page as fallback, then the assistant mentions that a QR " +
  "code points at the link and follows every change. Third: asked how the link is doing — analytics_summary " +
  "returns 4,812 human clicks in 24 hours, 38% from Instagram, top country Germany. Fourth: asked to confirm the " +
  "domain is still set up right — get_host with a live check shows DNS resolving and the certificate active, " +
  "then the assistant offers to set up the next campaign's links.";

/**
 * AgentHeroSection — the /ai-mcp flagship: a dark hero (content-height, like any
 * other dark band — NOT viewport-height) whose right column is a
 * pre-rendered assistant-chat panel cycling through one unified story told
 * in four connected MCP tool calls (create · update · analyze · check), each
 * ending with the assistant proactively suggesting the next useful thing —
 * the loop closes resolve → create. Static/reduced-motion/mobile default is
 * the finished "create" transcript, scenarios 2-4 present but hidden by
 * class, chips swapping instantly. On md+ with motion allowed, ONE master
 * GSAP timeline (paused, finite — NOT `repeat: -1`) plays the four
 * scenarios back to back as beats (user bubble → thinking → typing dots →
 * tool call → JSON → answer → follow-up → dissolve); each scenario's label
 * carries a `tl.call` that flushSync's the chip `active` state so the chip
 * row tracks the playlist. Once the last scenario finishes, a short
 * visibility-gated hold chains into a genuine `tl.restart()` (the
 * Switchboard's completion-chained autoplay idiom) rather than looping —
 * a native `repeat: -1` wrap stopped reliably re-firing the first label's
 * state-sync call after a while in real browsers. Chip clicks seek the
 * SAME clock (`tl.play(id)`) rather than driving a separate state-only
 * transition.
 *
 * Layout: the wrap's only spacing is the static 3-row top padding (nav row +
 * one extra dark row of breathing room + one free row — content starts row
 * 3) and mobile's pt-24/pb-16; `useWholeRowBlocks`/`useBandRowAlign` on the
 * content block then handle whole-row sizing, row alignment and the one
 * free row before the band ends exactly like every other dark band (no
 * bespoke min-height/viewport math).
 */
export default function AgentHeroSection() {
  const [active, setActive] = useState<ScenarioId>(SCENARIOS[0].id);
  const [enhanced, setEnhanced] = useState(false);

  const copyRef = useRef<HTMLDivElement>(null);
  const blockRef = useRef<HTMLDivElement>(null);
  // The chat panel — GSAP scope/selector root AND the IntersectionObserver
  // target (play while the panel itself is on screen).
  const panelRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<gsap.core.Timeline | null>(null);
  // Motion-enabled seek, set only while the matchMedia scope is active — the
  // NegotiationSwitchboard `fireRef` idiom (contextSafe isn't reachable
  // outside the useGSAP callback, so the wrapped function is stashed here).
  const selectRef = useRef<((id: ScenarioId) => void) | null>(null);
  // Completion-chained replay bookkeeping (the Switchboard's own
  // holdRef/visibleRef idiom) — see the timeline build below.
  const holdRef = useRef<number>(0);
  const visibleRef = useRef(false);

  useGSAP(
    (_context, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const q = gsap.utils.selector(panelRef);
        // Flip to enhanced (removes the static class-based hiding) and park
        // every scenario wrapper at autoAlpha 0 via GSAP in the SAME
        // synchronous effect. No flushSync here — this runs inside useGSAP's
        // layout effect, a React lifecycle phase where flushSync is illegal
        // ("React cannot flush when React is already rendering"). The
        // no-flash guarantee doesn't need it anyway: gsap.set applies its
        // inline autoAlpha:0 synchronously, before the browser paints, so by
        // the time React's re-render (dropping the opacity-0 class) commits,
        // the inline style is already covering every wrapper.
        setEnhanced(true);
        gsap.set(q("[data-scenario]"), { autoAlpha: 0 });

        // ONE finite pass through all four scenarios — NOT `repeat: -1`.
        // A native GSAP loop wrapping this timeline from its end back to
        // time 0 stopped reliably firing the first label's `.call()` (the
        // React state sync) after a while in real browsers — the arrival-
        // at-0-while-wrapping case is a known sharp edge, distinct from a
        // fresh `play()`/`restart()` from a stopped state. Instead: build a
        // finite timeline and chain each replay off `onComplete` (the
        // Switchboard's own completion-chained autoplay idiom, proven on
        // /dynamic-responses), so every cycle is a genuine fresh
        // `tl.restart()` with freshly re-armed labels/calls, never a wrap.
        const tl = gsap.timeline({
          paused: true,
          defaults: { ease: "power3.out", immediateRender: false },
        });
        for (const s of SCENARIOS) {
          const w = `[data-scenario="${s.id}"]`;
          tl.addLabel(s.id)
            .call(() => flushSync(() => setActive(s.id)))
            // Park this scenario's beats + json lines hidden WHILE its
            // wrapper is still invisible — the reset that keeps every loop
            // (and every mid-flight chip seek) landing in the same state.
            .set(q(`${w} [data-beat]`), { autoAlpha: 0, y: 14 })
            .set(q(`${w} [data-json-line]`), { autoAlpha: 0, x: -8 })
            .to(q(w), { autoAlpha: 1, duration: 0.3 })
            .to(q(`${w} [data-beat="user"]`), { autoAlpha: 1, y: 0, duration: 0.5 })
            .set(q(`${w} [data-beat="typing"]`), { autoAlpha: 1, y: 0 }, "+=0.5")
            .to(
              q(`${w} [data-beat="typing"] span`),
              { autoAlpha: 0.25, yoyo: true, repeat: 5, duration: 0.3, stagger: 0.12, ease: "sine.inOut" },
              "<",
            )
            .set(q(`${w} [data-beat="typing"]`), { autoAlpha: 0 }, "+=0.2")
            .to(q(`${w} [data-beat="a1"]`), { autoAlpha: 1, y: 0, duration: 0.4 }, "<")
            .to(q(`${w} [data-beat="tool"]`), { autoAlpha: 1, y: 0, duration: 0.5 }, "+=0.35")
            .to(
              q(`${w} [data-json-line]`),
              { autoAlpha: 1, x: 0, duration: 0.25, stagger: 0.13, ease: "power2.out" },
              "-=0.1",
            )
            .fromTo(
              q(`${w} [data-beat="tool"]`),
              { boxShadow: "0 0 0 1px rgba(124,92,255,0)" },
              { boxShadow: "0 0 0 1px rgba(124,92,255,0.55)", duration: 0.35, yoyo: true, repeat: 1, ease: "power2.inOut" },
            )
            .to(q(`${w} [data-beat="answer"]`), { autoAlpha: 1, y: 0, duration: 0.5 }, "+=0.3")
            .to(q(`${w} [data-beat="followup"]`), { autoAlpha: 1, y: 0, duration: 0.45 }, "+=0.6")
            .to(q(w), { autoAlpha: 0, duration: 0.5, ease: "power2.in" }, "+=4");
        }
        tlRef.current = tl;

        // Completion-chained replay: a constant hold after the LAST
        // scenario's dissolve, then a genuine `restart()` (time 0, all
        // labels/calls fresh) — never a `repeat` wrap. Gated on visibility
        // so an off-screen finish doesn't restart invisibly: if the panel
        // isn't currently intersecting when the hold elapses, do nothing
        // further here — the IntersectionObserver below restarts it itself
        // once the panel re-enters (see its own `progress() === 1` check).
        const REPLAY_HOLD_MS = 60;
        const scheduleReplay = () => {
          window.clearTimeout(holdRef.current);
          holdRef.current = window.setTimeout(() => {
            if (visibleRef.current) tl.restart();
          }, REPLAY_HOLD_MS);
        };
        tl.eventCallback("onComplete", scheduleReplay);

        // Runs only while the panel itself is on screen — same pattern as
        // DomainWinsSection's ShareCard loop (pause, don't kill, off-screen).
        // A finished (progress === 1) timeline re-entering the viewport gets
        // a fresh `restart()` rather than a no-op `play()` (which would just
        // sit at the end); anything still mid-cycle just resumes.
        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              visibleRef.current = e.isIntersecting;
              if (!e.isIntersecting) {
                tl.pause();
                return;
              }
              if (tl.progress() === 1) tl.restart();
              else tl.play();
            }),
          { threshold: 0.3 },
        );
        if (panelRef.current) io.observe(panelRef.current);

        // Chip seek: resume the SAME clock FROM the target label rather than
        // seeking + playing separately — play(label) re-fires that label's
        // tl.call, which fires on the next tick after play() unpauses, so
        // the chip highlight updates almost immediately. Works identically
        // seeking forward or backward, including right after a restart.
        // Cancels any pending scheduled replay first, so a manual pick
        // during the post-completion hold isn't undone by a stray restart.
        selectRef.current = contextSafe!((id: ScenarioId) => {
          window.clearTimeout(holdRef.current);
          tlRef.current?.play(id);
        });

        return () => {
          io.disconnect();
          window.clearTimeout(holdRef.current);
          tl.kill();
          tlRef.current = null;
          selectRef.current = null;
          setEnhanced(false);
        };
      });
    },
    { scope: panelRef },
  );

  const selectScenario = (id: ScenarioId) => {
    if (selectRef.current) {
      selectRef.current(id);
    } else {
      setActive(id);
    }
  };

  // Whole-row block sizing for the single content block — must settle
  // BEFORE the wrap-padding effect below measures the block's height.
  useWholeRowBlocks(blockRef);
  useBandRowAlign(blockRef);

  // Hero headline: masked SplitText line reveal (the FeatureHero/Switchboard
  // treatment) on the copy column's h1. No-op under reduced motion.
  useMaskedReveal(copyRef, { heading: "h1" });

  return (
    <SectionShell id="ref-agent-hero" band="dark" decoAvoid className="max-md:bg-[#14141c] md:pt-px md:pb-px">
      <div className="pt-24 pb-16 md:pb-0 md:pt-[calc(3*var(--grid-cell,72px))]">
        <div
          ref={blockRef}
          data-grid-block
          data-grid-fill={DARK_BAND}
          className="grid items-start gap-10 md:grid-cols-[1fr_1.1fr]"
        >
          {/* Left — hero copy. */}
          <div ref={copyRef} data-hero-copy className="min-w-0">
            <h1 className="text-5xl font-extrabold leading-[1.06] tracking-tight text-white md:mt-[36px] md:text-6xl">
              Feed agents{" "}
              <span className="relative inline-block whitespace-nowrap">
                <GradientText colors={["#d70e62", "#fa437f", "#ff7eae"]} animationSpeed={2} yoyo={false}>
                  structured data.
                </GradientText>
              </span>
            </h1>

            <p className="mt-5 max-w-[520px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-white/75">
              Connect your AI agent to rii.link over MCP — it creates links, sets routing, reads analytics and
              checks your domains, and every answer comes back as structured data.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button href={REGISTER_URL}>Start for free</Button>
              <Button href={routePath("docs")} variant="ghost-light">
                Read the developer docs
              </Button>
            </div>
          </div>

          {/* Right — the assistant-chat panel. */}
          <div className="min-w-0">
            <p className="sr-only">{SR_SUMMARY}</p>

            <div role="group" aria-label="Example conversations" className="mb-2.5 flex flex-wrap gap-1.5">
              {SCENARIOS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  aria-pressed={active === s.id}
                  onClick={() => selectScenario(s.id)}
                  className={`rounded-lg border px-2.5 py-1 font-jetbrains text-xs transition-colors ${
                    active === s.id
                      ? "border-brand-400/60 bg-brand-600/20 text-white"
                      : "border-white/10 bg-white/[0.04] text-white/55 hover:text-white/80"
                  }`}
                >
                  {s.chip}
                </button>
              ))}
            </div>

            <div
              ref={panelRef}
              aria-hidden="true"
              data-chat-panel
              className="overflow-hidden rounded-ref-lg border border-white/10 bg-[#101017] shadow-ref-lg"
            >
              <div className="flex items-center gap-[11px] border-b border-white/10 bg-white/[0.03] px-[18px] py-3.5">
                <span className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-[linear-gradient(140deg,var(--color-data-violet),#4a32a8)] text-[15px] text-white">
                  ✦
                </span>
                <div>
                  <div className="text-sm font-semibold text-white">Assistant</div>
                  <div className="text-xs text-white/50">working over MCP</div>
                </div>
                <span className="ml-auto rounded-[7px] border border-white/10 px-[9px] py-1 font-jetbrains text-[11px] text-white/60">
                  mcp.rii.link · JSON
                </span>
              </div>

              <div className="grid p-[18px]" style={{ minHeight: 446 }}>
                {SCENARIOS.map((s) => (
                  <div
                    key={s.id}
                    data-scenario={s.id}
                    className={`col-start-1 row-start-1 flex min-w-0 flex-col gap-3.5 ${
                      !enhanced && active !== s.id ? "pointer-events-none opacity-0" : ""
                    }`}
                  >
                    <div
                      data-beat="user"
                      className="max-w-[90%] self-end rounded-[13px_13px_4px_13px] bg-brand-600 px-3.5 py-2.5 text-sm text-white"
                    >
                      {s.user}
                    </div>

                    <div data-beat="a1" className="self-start text-sm text-white/90">
                      {s.thinking}
                    </div>

                    {/* Motion-only chrome — never state-toggled, always
                        invisible in the static/non-enhanced path. */}
                    <div data-beat="typing" className="flex gap-1 self-start px-1 opacity-0">
                      <span className="h-[5px] w-[5px] rounded-full bg-white/40" />
                      <span className="h-[5px] w-[5px] rounded-full bg-white/40" />
                      <span className="h-[5px] w-[5px] rounded-full bg-white/40" />
                    </div>

                    <div
                      data-beat="tool"
                      className="min-w-0 overflow-hidden rounded-[11px] border border-white/10 bg-[#0d0d12]"
                    >
                      <div className="flex items-center gap-[9px] border-b border-white/10 bg-white/[0.04] px-3 py-[9px] font-jetbrains text-xs">
                        <span className="shrink-0 text-data-violet">✦</span>
                        <span className="shrink-0 font-semibold text-white">
                          {s.call.method} {s.call.target}
                        </span>
                        {/* The flexible middle: only this may ellipsize under
                            narrow widths — method+target and the status chip
                            (both shrink-0) always stay fully visible. */}
                        <span className="min-w-0 flex-1 truncate text-white/50">{s.call.meta}</span>
                        <span className="ml-auto shrink-0 font-semibold text-data-green">{s.call.status}</span>
                      </div>
                      <pre className="m-0 min-w-0 px-3.5 py-[11px] font-jetbrains text-[11.5px] leading-[1.7] whitespace-pre-wrap [overflow-wrap:anywhere]">
                        {s.json.map((line, i) => (
                          <span data-json-line key={i} className="block text-white/70">
                            {line}
                          </span>
                        ))}
                      </pre>
                    </div>

                    <div data-beat="answer" className="self-start text-sm text-white/90">
                      {s.answer}
                    </div>

                    {/* Assistant-initiative beat: proactively suggests the
                        next useful thing — visually distinct from the
                        answer (violet "✦" prefix, dimmer body). */}
                    <div data-beat="followup" className="self-start text-sm text-white/70">
                      <span className="text-data-violet">✦ </span>
                      {s.followup}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
